<template>
  <HeadlessPopover v-slot="{ open, close: closePopover }" class="relative">
    <HeadlessPopoverButton
      ref="buttonRef"
      as="template"
      :open
      :close="closePopover"
      @focus="handleFocus"
      @mousedown="handleMousedown"
    >
      <slot name="button" :is-open :toggle></slot>
    </HeadlessPopoverButton>

    <RedesignWebccDialog
      v-if="$screen.isSmall"
      full-screen
      :open="isOpen"
      @close="close"
    >
      <slot :is-open :toggle></slot>
    </RedesignWebccDialog>

    <HeadlessPopoverPanel
      v-else
      class="z-10 bg-bgr absolute mt-2 rounded-lg border border-bgr-300 shadow-lg"
    >
      <slot :is-open :toggle></slot>
    </HeadlessPopoverPanel>
  </HeadlessPopover>
</template>

<script setup lang="ts">
import type { PopoverButton } from '@headlessui/vue'
import { isBoolean, isFunction } from 'lodash-es'

defineExpose({ toggle })

type SlotProps = {
  isOpen: boolean
  toggle(value?: boolean | undefined): void
}

defineSlots<{
  button(props: SlotProps): unknown
  default(props: SlotProps): unknown
}>()

const emit = defineEmits<{ close: [] }>()

const buttonRef = shallowRef<InstanceType<typeof PopoverButton>>()

const { focused } = useFocus(buttonRef)

const isClicking = ref(false)
const isOpen = ref(false)

/**
 * Observing the custom `"open"` attribute  to update `isOpen` state.
 *
 * Due to the Headless {@link https://headlessui.com/v1/vue/popover | Popover} API
 * does not provide a way to determine whether a popup is currently open or not.
 */
const openAttrObserver = useMutationObserver(
  buttonRef,
  () => (isOpen.value = !!buttonRef.value?.$attrs.open),
  { attributes: true, attributeFilter: ['open'] },
)

watch(isOpen, (newValue) => !newValue && emit('close'))

onBeforeUnmount(() => openAttrObserver.stop())

function handleMousedown() {
  isClicking.value = !focused.value
}

function handleFocus() {
  isClicking.value = false
}

function close() {
  handleMousedown()
  const closeFn = buttonRef.value?.$attrs.close
  if (!isFunction(closeFn)) return
  closeFn()
}

function toggle(value?: boolean | undefined) {
  if (isClicking.value) return

  if (isBoolean(value) && value === isOpen.value) return

  if (isOpen.value) {
    close()
  } else {
    buttonRef.value?.$el.click()
  }
}
</script>
